/**
 * DossierCompetences.jsx
 * ─────────────────────────────────────────────────────────────────
 * Génère le PDF côté navigateur avec jsPDF.
 *
 * Résolution des logos (ordre de priorité) :
 *   1. Fichier local PNG  → /public/assets/img/techno/{nom}.png
 *   2. SVG inline         → concepts métier (cybersecurite, reseau…)
 *   3. Devicon CDN        → technos standard dev/IT
 *
 * Déduplication : chaque techno n'apparaît qu'une seule fois,
 * peu importe le nombre d'expériences qui la référencent.
 *
 * npm install jspdf
 * ─────────────────────────────────────────────────────────────────
 */

import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import './DossierCompetences.css';

// ══════════════════════════════════════════════════════════════════
// 📁 CHEMIN DES LOGOS LOCAUX
//    Convention de nommage : lowercase, espaces/tirets → underscore
//    Exemples : stormshield.png · power_apps.png · vuejs.png
// ══════════════════════════════════════════════════════════════════
const LOCAL_LOGOS_PATH = '/assets/img/techno';

/** Normalise un nom de techno en slug de fichier PNG local. */
function toLocalSlug(name) {
  return name.trim().toLowerCase()
    .replace(/[\s\-]+/g, '_')   // espaces et tirets → underscore
    .replace(/[.#+@]/g, '');    // retire les caractères spéciaux
}

/** Tente de trouver le PNG local. Retourne l'URL ou null. */
async function resolveLocalLogo(name) {
  const url = `${LOCAL_LOGOS_PATH}/${toLocalSlug(name)}.png`;
  try {
    const r = await fetch(url, { method: 'HEAD' });
    if (r.ok) return url;
  } catch (_) {}
  return null;
}

// ══════════════════════════════════════════════════════════════════
// 🔧 EXCLUSIONS — technos à ne jamais afficher dans la grille
// ══════════════════════════════════════════════════════════════════
const TECH_EXCLUDE = new Set([
  'wordpress', 'jquery', 'redux', 'grunt',
  // Concepts génériques exclus par défaut (pas de logo pertinent)
  'reseau', 'réseau', 'logiciel', 'logiciels',
]);

// ══════════════════════════════════════════════════════════════════
// ✏️ CORRECTIONS TYPOGRAPHIQUES
// ══════════════════════════════════════════════════════════════════
const TYPO_FIX = {
  // Orthographe
  ngnix:        'nginx',
  postgre:      'postgresql',
  postgress:    'postgresql',
  mongo:        'mongodb',
  // Accents / variantes françaises
  'c#':         'csharp',
  'c++':        'cplusplus',
  // Noms raccourcis courants
  js:           'javascript',
  ts:           'typescript',
  py:           'python',
  node:         'nodejs',
  react:        'react',
  vue:          'vuejs',
  angular:      'angularjs',
  next:         'nextjs',
  nuxt:         'nuxtjs',
  k8s:          'kubernetes',
  tf:           'terraform',
  psql:         'postgresql',
  pg:           'postgresql',
  mssql:        'microsoftsqlserver',
  sqlserver:    'microsoftsqlserver',
  gcp:          'googlecloud',
  aws:          'amazonwebservices',
  // Casse / variantes
  powershell:   'powershell',
  vscode:       'vscode',
  '.net':       'dotnetcore',
  dotnet:       'dotnetcore',
};

// ══════════════════════════════════════════════════════════════════
// 🏷️ CORRECTIONS D'AFFICHAGE — nom affiché dans la grille PDF
//    Clé = normKey(nom brut), valeur = nom corrigé à afficher
// ══════════════════════════════════════════════════════════════════
const DISPLAY_NAME_FIX = {
  power_automate:  'Power Automate',
  powerautomate:   'Power Automate',
  powerbi:         'Power BI',
  power_bi:        'Power BI',
  powerapps:       'Power Apps',
  power_apps:      'Power Apps',
};

/** Retourne le nom corrigé à afficher, ou le nom original si pas de correction. */
function fixDisplayName(rawName) {
  const key = normKey(rawName).replace(/_/g, '');  // normalise aussi les underscores
  const keyWithUnder = rawName.trim().toLowerCase().replace(/[\s\-]+/g, '_').replace(/[.#+@]/g, '');
  return DISPLAY_NAME_FIX[key] || DISPLAY_NAME_FIX[keyWithUnder] || rawName;
}

// ══════════════════════════════════════════════════════════════════
// 🎨 SVG INLINE — fallback pour concepts métier sans fichier PNG
// ══════════════════════════════════════════════════════════════════
const INLINE_SVG = {
  documentation: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="8" y1="9" x2="11" y2="9"/></svg>`,
  cybersecurite: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
  logiciel:      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`,
  reseau:        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="6" height="6" rx="1"/><rect x="16" y="2" width="6" height="6" rx="1"/><rect x="9" y="16" width="6" height="6" rx="1"/><line x1="5" y1="8" x2="5" y2="14"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="5" y1="14" x2="12" y2="18"/><line x1="19" y1="14" x2="12" y2="18"/></svg>`,
  infrastructure:`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="5" rx="1"/><rect x="2" y="10" width="20" height="5" rx="1"/><rect x="2" y="17" width="20" height="5" rx="1"/><circle cx="6" cy="5.5" r="1" fill="#1a6fd4"/><circle cx="6" cy="12.5" r="1" fill="#1a6fd4"/><circle cx="6" cy="19.5" r="1" fill="#1a6fd4"/></svg>`,
  supervision:   `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
  audit:         `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>`,
  support:       `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  formation:     `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#1a6fd4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>`,
};

function svgToUrl(svgString) {
  // jsPDF ne peut pas charger les blob: URLs — on encode en base64 data URL
  const encoded = btoa(unescape(encodeURIComponent(svgString)));
  return `data:image/svg+xml;base64,${encoded}`;
}

// ══════════════════════════════════════════════════════════════════
// 🔗 DEVICON CDN — fallback final
// ══════════════════════════════════════════════════════════════════
const DEVICON_BASE     = 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons';
const DEVICON_VARIANTS = ['plain', 'original', 'plain-wordmark', 'original-wordmark', 'line'];

const TECH_ALIASES = {
  js: 'javascript', javascript: 'javascript',
  ts: 'typescript', typescript: 'typescript',
  py: 'python',     python: 'python',
  cs: 'csharp',     csharp: 'csharp', 'c#': 'csharp',
  cplusplus: 'cplusplus', 'c++': 'cplusplus',
  golang: 'go', go: 'go',
  rb: 'ruby', ruby: 'ruby',
  kt: 'kotlin', kotlin: 'kotlin',
  swift: 'swift', rust: 'rust', scala: 'scala',
  html: 'html5', html5: 'html5',
  css: 'css3', css3: 'css3',
  sass: 'sass', scss: 'sass', less: 'less',
  react: 'react', reactjs: 'react', reactnative: 'react',
  vue: 'vuejs', vuejs: 'vuejs',
  angular: 'angularjs', angularjs: 'angularjs',
  svelte: 'svelte', nextjs: 'nextjs', next: 'nextjs',
  nuxtjs: 'nuxtjs', nuxt: 'nuxtjs',
  gatsby: 'gatsby', astro: 'astro',
  bootstrap: 'bootstrap', tailwind: 'tailwindcss', tailwindcss: 'tailwindcss',
  materialui: 'materialui', mui: 'materialui',
  nodejs: 'nodejs', node: 'nodejs',
  express: 'express', expressjs: 'express',
  nestjs: 'nestjs', nest: 'nestjs',
  django: 'django', flask: 'flask', fastapi: 'fastapi',
  rails: 'rails', spring: 'spring', laravel: 'laravel',
  symfony: 'symfony', dotnet: 'dotnetcore', '.net': 'dotnetcore',
  graphql: 'graphql', kafka: 'apachekafka',
  mysql: 'mysql', postgresql: 'postgresql', postgres: 'postgresql',
  mongodb: 'mongodb', redis: 'redis', sqlite: 'sqlite',
  mariadb: 'mariadb', oracle: 'oracle',
  mssql: 'microsoftsqlserver', sqlserver: 'microsoftsqlserver',
  cassandra: 'cassandra', dynamodb: 'dynamodb',
  firebase: 'firebase', supabase: 'supabase', elasticsearch: 'elasticsearch',
  docker: 'docker', kubernetes: 'kubernetes', k8s: 'kubernetes',
  helm: 'helm', terraform: 'terraform', ansible: 'ansible',
  jenkins: 'jenkins', github: 'github', gitlab: 'gitlab',
  bitbucket: 'bitbucket', nginx: 'nginx', apache: 'apache',
  aws: 'amazonwebservices', azure: 'azure',
  gcp: 'googlecloud', googlecloud: 'googlecloud',
  heroku: 'heroku', vercel: 'vercel', netlify: 'netlify',
  cloudflare: 'cloudflare', linux: 'linux', ubuntu: 'ubuntu',
  debian: 'debian', centos: 'centos', bash: 'bash', powershell: 'powershell',
  git: 'git', vscode: 'vscode', vim: 'vim', figma: 'figma',
  jira: 'jira', confluence: 'confluence', notion: 'notion', slack: 'slack',
  npm: 'npm', yarn: 'yarn', webpack: 'webpack', vite: 'vitejs',
  jest: 'jest', cypress: 'cypress', playwright: 'playwright',
  prisma: 'prisma', sequelize: 'sequelize',
  pandas: 'pandas', numpy: 'numpy', tensorflow: 'tensorflow',
  pytorch: 'pytorch', jupyter: 'jupyter', opencv: 'opencv',
  flutter: 'flutter', electron: 'electron', unity: 'unity',
  threejs: 'threejs', d3: 'd3js', chartjs: 'chartjs',
  rabbitmq: 'rabbitmq', cisco: 'cisco', vmware: 'vmware', grafana: 'grafana',
  perl: 'perl',
};

function normKey(name) {
  return name.toLowerCase().replace(/[\s.\-_#+@]/g, '');
}

async function resolveDevicon(rawName, manifest) {
  const key = normKey(rawName);
  const aliasSlug = TECH_ALIASES[key];
  if (aliasSlug) {
    const e = manifest.find(m => m.name.toLowerCase() === aliasSlug);
    if (e) return { slug: e.name.toLowerCase(), versions: e.versions?.svg || [] };
  }
  let e = manifest.find(m => normKey(m.name) === key);
  if (e) return { slug: e.name.toLowerCase(), versions: e.versions?.svg || [] };
  e = manifest.find(m => (m.altnames || []).some(a => normKey(a) === key));
  if (e) return { slug: e.name.toLowerCase(), versions: e.versions?.svg || [] };
  if (key.length >= 3) {
    e = manifest.find(m => { const s = normKey(m.name); return s.includes(key) || key.includes(s); });
    if (e) return { slug: e.name.toLowerCase(), versions: e.versions?.svg || [] };
  }
  for (const c of [key, key.replace(/js$/, ''), key + 'js'].filter(Boolean)) {
    for (const v of DEVICON_VARIANTS) {
      try {
        const r = await fetch(`${DEVICON_BASE}/${c}/${c}-${v}.svg`, { method: 'HEAD' });
        if (r.ok) return { slug: c, versions: [v] };
      } catch (_) {}
    }
  }
  return { slug: key, versions: [] };
}

function deviconUrl(slug, versions) {
  for (const v of DEVICON_VARIANTS) {
    if (versions.includes(v)) return `${DEVICON_BASE}/${slug}/${slug}-${v}.svg`;
  }
  return versions.length ? `${DEVICON_BASE}/${slug}/${slug}-${versions[0]}.svg` : null;
}

// ══════════════════════════════════════════════════════════════════
// 🔍 EXTRACTION + DÉDUPLICATION + RÉSOLUTION
// ══════════════════════════════════════════════════════════════════
async function extractAndResolveTechs(experiences) {
  const seen     = new Set();
  const rawNames = [];

  for (const exp of experiences) {
    for (const t of (exp.modal?.techs || [])) {
      const key = normKey(t);
      if (!seen.has(key) && !TECH_EXCLUDE.has(key) && !TECH_EXCLUDE.has(t.trim().toLowerCase())) {
        seen.add(key);
        rawNames.push(t.trim());
      }
    }
  }

  if (rawNames.length === 0) return [];

  let manifest = [];
  try {
    const r = await fetch('https://cdn.jsdelivr.net/gh/devicons/devicon@latest/devicon.json');
    manifest = await r.json();
  } catch (_) {}

  const resolved = await Promise.all(
    rawNames.map(async rawName => {
      const key = normKey(rawName);

      const localUrl = await resolveLocalLogo(rawName);
      if (localUrl) {
        return { name: fixDisplayName(rawName), img: localUrl, _src: 'local' };
      }

      if (INLINE_SVG[key]) {
        return { name: fixDisplayName(rawName), img: svgToUrl(INLINE_SVG[key]), _src: 'inline' };
      }

      const fixedName = TYPO_FIX[key] ? TYPO_FIX[key] : rawName;
      const { slug, versions } = await resolveDevicon(fixedName, manifest);
      return { name: fixDisplayName(rawName), slug, versions, _src: versions.length ? 'devicon' : 'none' };
    })
  );

  console.group('[DossierCompetences] Technos résolues (%d unique%s)',
    resolved.length, resolved.length > 1 ? 's' : '');
  console.table(resolved.map(t => ({
    Nom:    t.name,
    Source: t._src,
    Détail: t.img
      ? t.img.startsWith('blob:') ? 'SVG blob' : t.img
      : deviconUrl(t.slug, t.versions) || '⚠ aucun logo trouvé',
  })));
  console.groupEnd();

  return resolved;
}

// ── Palette ───────────────────────────────────────────────────────
const C = {
  white:     [255, 255, 255],
  offWhite:  [247, 249, 252],
  blue:      [26,  111, 212],
  blueLight: [235, 242, 252],
  ink:       [13,  27,  42],
  slate:     [61,  81,  102],
  muted:     [122, 144, 164],
  border:    [212, 227, 245],
  chipBg:    [221, 233, 247],
};

const W     = 210;
const MARG  = 12;
const COL_L = 38;

const setFill = (doc, rgb) => doc.setFillColor(...rgb);
const setDraw = (doc, rgb) => doc.setDrawColor(...rgb);
const setTxt  = (doc, rgb) => doc.setTextColor(...rgb);
const setFont = (doc, style, size) => { doc.setFont('helvetica', style); doc.setFontSize(size); };

async function loadImage(src) {
  const isSvgUrl = src && !src.startsWith('data:') && (
    src.includes('.svg') || src.includes('jsdelivr') || src.includes('devicon')
  );

  if (isSvgUrl) {
    try {
      const resp = await fetch(src);
      if (!resp.ok) return null;
      let svgText = await resp.text();
      if (!svgText.includes('width=') || !svgText.includes('height=')) {
        svgText = svgText.replace('<svg', '<svg width="64" height="64"');
      }
      const encoded = btoa(unescape(encodeURIComponent(svgText)));
      src = `data:image/svg+xml;base64,${encoded}`;
    } catch (_) { return null; }
  }

  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const nw = img.naturalWidth  > 0 ? img.naturalWidth  : 64;
      const nh = img.naturalHeight > 0 ? img.naturalHeight : 64;
      const canvas = document.createElement('canvas');
      canvas.width = nw; canvas.height = nh;
      canvas.getContext('2d').drawImage(img, 0, 0, nw, nh);
      resolve({ b64: canvas.toDataURL('image/png'), nw, nh });
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function fitSize(nw, nh, maxW, maxH) {
  const ratio = nw / nh;
  let w = maxW, h = w / ratio;
  if (h > maxH) { h = maxH; w = h * ratio; }
  return { w, h };
}

async function addImageFit(doc, src, boxX, boxY, boxW, boxH) {
  const res = await loadImage(src);
  if (!res) return false;
  const { b64, nw, nh } = res;
  const { w, h } = fitSize(nw, nh, boxW, boxH);
  const x = boxX + (boxW - w) / 2;
  const y = boxY + (boxH - h) / 2;
  try { doc.addImage(b64, 'PNG', x, y, w, h); return true; } catch (_) { return false; }
}

// ── Hero image (page 1) ───────────────────────────────────────────
async function drawHeroImage(doc, name, role = '', contacts = []) {
  const H = 55;
  const fondPaths = ['/fond-dct.png', '/assets/img/fond-dct.png', '/public/fond-dct.png', 'fond-dct.png'];
  let heroRes = null;
  for (const path of fondPaths) {
    try {
      heroRes = await loadImage(path);
      if (heroRes) break;
    } catch (_) {}
  }
  if (heroRes) {
    doc.addImage(heroRes.b64, 'PNG', 0, 0, W, H);
  } else {
    setFill(doc, [10, 80, 175]); doc.rect(0, 0, W, H, 'F');
    setFill(doc, [5, 55, 130]);  doc.rect(0, H * 0.55, W, H * 0.45, 'F');
  }
  setFill(doc, [0, 0, 0]);
  doc.setGState(doc.GState({ opacity: 0.38 }));
  doc.rect(0, 0, W, H, 'F');
  doc.setGState(doc.GState({ opacity: 1 }));

  setTxt(doc, C.white); setFont(doc, 'bold', 22);
  doc.text(name, MARG, 18);
  setFont(doc, 'normal', 10); setTxt(doc, [200, 220, 245]);
  doc.text(role, MARG, 26);
  setDraw(doc, [180, 200, 230]); doc.setLineWidth(0.4);
  doc.line(MARG, 30, 30, 30);

  if (contacts.length) {
    let cx = MARG;
    contacts.forEach(c => {
      const lbl = c.label || '';
      setFont(doc, 'normal', 7.5);
      const bw = doc.getTextWidth(lbl) + 8;
      setFill(doc, [50, 90, 160]);
      doc.setGState(doc.GState({ opacity: 0.85 }));
      doc.roundedRect(cx, 33, bw, 5.5, 2, 2, 'F');
      doc.setGState(doc.GState({ opacity: 1 }));
      setTxt(doc, [220, 235, 255]);
      doc.text(lbl, cx + 4, 37.2);
      cx += bw + 4;
      if (cx > W - 20) cx = MARG;
    });
  }
  return H + 4;
}

// ── Hero compact (pages 2+) ───────────────────────────────────────
function drawHeroCompact(doc, title) {
  setFill(doc, C.blue); doc.rect(0, 0, W, 20, 'F');
  setTxt(doc, C.white); setFont(doc, 'bold', 13);
  doc.text(title, MARG, 13);
  return 24;
}

// ── Titre de section ──────────────────────────────────────────────
function drawSectionTitle(doc, title, y) {
  y += 4;
  const H = 9;
  setFill(doc, C.blue);
  doc.rect(0, y, W, H, 'F');
  setFill(doc, [10, 80, 175]);
  doc.rect(0, y, 3, H, 'F');
  setFont(doc, 'bold', 10);
  setTxt(doc, C.white);
  doc.text(title, MARG + 2, y + 6.2);
  return y + H + 4;
}

// ── Grille Technologies ───────────────────────────────────────────
async function drawTechGrid(doc, technologies, y, sectionLabel = 'Technologies') {
  y = drawSectionTitle(doc, sectionLabel, y);

  const COLS   = 6;
  const CELL_W = (W - MARG * 2) / COLS;
  const IMG_SZ = 9;
  const BOX_H  = IMG_SZ + 2;
  const CELL_H = BOX_H + 8;
  const GAP    = 3;

  for (let i = 0; i < technologies.length; i++) {
    const tech = technologies[i];
    const col  = i % COLS;
    const row  = Math.floor(i / COLS);
    const cx   = MARG + col * CELL_W;
    const cy   = y + row * (CELL_H + GAP);

    setFill(doc, C.offWhite); setDraw(doc, C.border); doc.setLineWidth(0.25);
    doc.roundedRect(cx + 1, cy, CELL_W - 2, CELL_H, 2, 2, 'FD');

    const imgSrc = tech.img || deviconUrl(tech.slug || '', tech.versions || []);
    if (imgSrc) {
      const imgX = cx + (CELL_W - IMG_SZ) / 2;
      const imgY = cy + 1.5;
      await addImageFit(doc, imgSrc, imgX, imgY, IMG_SZ, IMG_SZ);
    }

    setFont(doc, 'normal', 5.5); setTxt(doc, C.ink);
    const label   = tech.name || '';
    const nameW   = doc.getTextWidth(label);
    const labelX  = cx + (CELL_W - nameW) / 2;
    doc.text(label, labelX, cy + BOX_H + 5.5);
  }

  return y + Math.ceil(technologies.length / COLS) * (CELL_H + GAP) + 6;
}

// ── Footer ────────────────────────────────────────────────────────
function drawFooter(doc, authorName, pageNum, totalPages) {
  const pH = 297;
  setFill(doc, C.blue); doc.rect(0, pH - 10, W, 10, 'F');
  setTxt(doc, [200, 220, 240]); setFont(doc, 'normal', 7);
  doc.text(`${authorName} — Dossier de compétences`, MARG, pH - 3.5);
  doc.text(`${pageNum} / ${totalPages}`, W - MARG, pH - 3.5, { align: 'right' });
}

// ── Item parcours ─────────────────────────────────────────────────
async function drawParcoursItem(doc, item, media, y) {
  const { period = '', title = '', org = '', desc = '' } = item;
  const xLine  = MARG + COL_L + 2;
  const xRight = xLine + 4;
  const wRight = W - MARG - xRight;

  if (media?.img) await addImageFit(doc, media.img, MARG, y, 10, 10);

  setFont(doc, 'bold', 7); setTxt(doc, C.blue);
  doc.text(doc.splitTextToSize(period, COL_L - 2), MARG, y + 12);

  setDraw(doc, C.border); doc.setLineWidth(0.5);
  doc.line(xLine, y, xLine, y + 22);

  setFont(doc, 'bold', 9); setTxt(doc, C.ink);
  const titleLines = doc.splitTextToSize(title, wRight);
  doc.text(titleLines, xRight, y + 4);

  setFont(doc, 'normal', 8); setTxt(doc, C.blue);
  const orgY = y + 4 + titleLines.length * 4.5;
  doc.text(org, xRight, orgY);

  setFont(doc, 'normal', 8); setTxt(doc, C.slate);
  const descLines = desc ? doc.splitTextToSize(desc, wRight) : [];
  if (descLines.length) doc.text(descLines, xRight, orgY + 5);

  return y + Math.max(22, 4 + titleLines.length * 4.5 + 5 + descLines.length * 4 + 4) + 4;
}

// ── Carte expérience ──────────────────────────────────────────────
async function drawExpCard(doc, exp, y, lbl) {
  const { period = '', title = '', img = '' } = exp;
  const modal    = exp.modal || {};
  const tasks    = modal.tasks  || [];
  const techs    = modal.techs  || [];
  const type     = modal.type   || '';
  const location = modal.location || '';

  let bodyH = 6;
  tasks.forEach(t => { bodyH += Math.ceil(t.length / 70) * 4.5 + 1; });
  if (tasks.length) bodyH += 10;
  if (techs.length) bodyH += 8;
  bodyH = Math.max(bodyH, 12);

  const cardH = 17 + bodyH + 5;

  setFill(doc, C.blueLight); setDraw(doc, C.border); doc.setLineWidth(0.3);
  doc.roundedRect(MARG, y, W - MARG * 2, 16, 2, 2, 'FD');

  if (img) await addImageFit(doc, img, W - MARG - 14, y + 1, 12, 12);

  setFont(doc, 'bold', 7.5); setTxt(doc, C.blue);
  doc.text(period, MARG + 4, y + 5);

  setFont(doc, 'bold', 9); setTxt(doc, C.ink);
  const titleLines = doc.splitTextToSize(title, W - MARG * 2 - 20);
  doc.text(titleLines[0] || '', MARG + 4, y + 11);

  let bx = MARG + 4;
  [type, location].filter(Boolean).forEach(badge => {
    setFont(doc, 'normal', 6.5);
    const bw = doc.getTextWidth(badge) + 5;
    setFill(doc, C.chipBg); doc.roundedRect(bx, y + 12.5, bw, 3.5, 1, 1, 'F');
    setTxt(doc, C.blue); doc.text(badge, bx + 2.5, y + 15.3);
    bx += bw + 2;
  });

  const yBody = y + 17;
  setFill(doc, C.offWhite); doc.roundedRect(MARG, yBody, W - MARG * 2, bodyH, 2, 2, 'F');

  let cy = yBody + 5;
  const wText = W - MARG * 2 - 12;

  if (tasks.length) {
    setFont(doc, 'bold', 7.5); setTxt(doc, C.ink);
    doc.text(lbl.missions, MARG + 4, cy); cy += 5;
    tasks.forEach(task => {
      setFill(doc, C.blue); doc.circle(MARG + 6.5, cy - 1, 0.8, 'F');
      setFont(doc, 'normal', 7.5); setTxt(doc, C.slate);
      const lines = doc.splitTextToSize(task, wText);
      doc.text(lines, MARG + 10, cy);
      cy += lines.length * 4.5 + 0.5;
    });
  }

  if (techs.length) {
    setFont(doc, 'italic', 7); setTxt(doc, C.muted);
    doc.text(doc.splitTextToSize(`${lbl.techs} : ${techs.join(', ')}`, wText), MARG + 4, cy + 2);
  }

  return y + cardH + 2;
}

// ── Carte certification ───────────────────────────────────────────
async function drawCertCard(doc, cert, y) {
  setFill(doc, C.offWhite); setDraw(doc, C.blue); doc.setLineWidth(0.6);
  doc.roundedRect(MARG, y, W - MARG * 2, 18, 2, 2, 'FD');
  setFill(doc, C.blue); doc.rect(MARG, y, 2, 18, 'F');

  if (cert.img) await addImageFit(doc, cert.img, MARG + 5, y + 3, 12, 12);

  const xText = MARG + 22;
  setFont(doc, 'bold', 9); setTxt(doc, C.ink); doc.text(cert.alt || '', xText, y + 6);
  setFont(doc, 'normal', 8); setTxt(doc, C.muted); doc.text(cert.from || '', xText, y + 11);
  if (cert.href) {
    setFont(doc, 'normal', 7.5); setTxt(doc, C.blue);
    doc.textWithLink('Voir le certificat →', xText, y + 15.5, { url: cert.href });
  }
  return y + 22;
}

// ── Génération PDF principale ─────────────────────────────────────
async function generatePDF({ profile, parcoursItems, parcoursMedia, experiences, technologies, lang, labels }) {
  const name     = profile.name     || 'Prénom NOM';
  const role     = profile.role     || '';
  const intro    = profile.intro    || '';
  const contacts = profile.contacts || [];

  const lbl = {
    parcours:    labels.parcours    || 'Parcours',
    experiences: labels.experiences || 'Expériences',
    certifs:     labels.certifs     || 'Certifications',
    techs:       labels.techs       || 'Technologies',
    missions:    labels.missions    || 'Missions & tâches',
  };

  const techList = (technologies && technologies.length > 0)
    ? technologies
    : await extractAndResolveTechs(experiences);

  const allCerts = [];
  parcoursMedia.forEach((media, i) => {
    (media?.certs || []).forEach(c => allCerts.push({ ...c, from: (parcoursItems[i] || {}).org || '' }));
  });
  experiences.forEach(exp => { if (exp.cert) allCerts.push({ ...exp.cert, from: exp.title }); });

  let totalPages = 1;
  if (parcoursItems.length > 0) totalPages++;
  if (parcoursItems.length > 5) totalPages++;
  if (experiences.length > 0)   totalPages++;
  totalPages++;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  let y, pageNum;

  // ── PAGE 1 : Header + intro + Technologies ────────────────────
  y = await drawHeroImage(doc, name, role, contacts);

  if (intro) {
    setFont(doc, 'normal', 8.5);
    const introLines = doc.splitTextToSize(intro, W - MARG * 2 - 10);
    const introH = introLines.length * 4.5 + 10;
    setFill(doc, C.blueLight);
    doc.rect(0, y, W, introH, 'F');
    setFill(doc, C.blue);
    doc.rect(0, y, 4, introH, 'F');
    setTxt(doc, C.slate);
    doc.text(introLines, MARG + 2, y + 7);
    y += introH + 6;
  }

  if (techList.length > 0) y = await drawTechGrid(doc, techList, y, lbl.techs);
  drawFooter(doc, name, 1, totalPages);

  // ── PAGE 2 : Parcours ─────────────────────────────────────────
  pageNum = 2;
  if (parcoursItems.length > 0) {
    doc.addPage();
    y = drawHeroCompact(doc, lbl.parcours);
    y = drawSectionTitle(doc, lbl.parcours, y);
    for (const [i, item] of parcoursItems.slice(0, 5).entries()) {
      if (y > 270) {
        drawFooter(doc, name, pageNum, totalPages); doc.addPage(); pageNum++;
        y = drawHeroCompact(doc, lbl.parcours + ' (suite)');
        y = drawSectionTitle(doc, lbl.parcours, y);
      }
      y = await drawParcoursItem(doc, item, parcoursMedia[i], y);
    }
    drawFooter(doc, name, pageNum, totalPages); pageNum++;
  }

  // ── PAGE : Parcours suite ─────────────────────────────────────
  if (parcoursItems.length > 5) {
    doc.addPage();
    y = drawHeroCompact(doc, lbl.parcours + ' (suite)');
    y = drawSectionTitle(doc, lbl.parcours, y);
    for (const [i, item] of parcoursItems.slice(5).entries()) {
      if (y > 270) {
        drawFooter(doc, name, pageNum, totalPages); doc.addPage(); pageNum++;
        y = drawHeroCompact(doc, lbl.parcours + ' (suite)');
        y = drawSectionTitle(doc, lbl.parcours, y);
      }
      y = await drawParcoursItem(doc, item, parcoursMedia[5 + i], y);
    }
    drawFooter(doc, name, pageNum, totalPages); pageNum++;
  }

  // ── PAGE : Expériences ────────────────────────────────────────
  if (experiences.length > 0) {
    doc.addPage();
    y = drawHeroCompact(doc, lbl.experiences);
    y = drawSectionTitle(doc, lbl.experiences, y);
    for (const exp of experiences) {
      const tasks = exp.modal?.tasks || [];
      const techs = exp.modal?.techs || [];
      const estH  = 17 + 10 + tasks.length * 10 + (techs.length ? 8 : 0) + 10;
      if (y + estH > 280) {
        drawFooter(doc, name, pageNum, totalPages); doc.addPage(); pageNum++; totalPages++;
        y = drawHeroCompact(doc, lbl.experiences + ' (suite)');
        y = drawSectionTitle(doc, lbl.experiences, y);
      }
      y = await drawExpCard(doc, exp, y, lbl);
    }
    drawFooter(doc, name, pageNum, totalPages); pageNum++;
  }

  // ── PAGE : Certifications ─────────────────────────────────────
  doc.addPage();
  y = drawHeroCompact(doc, lbl.certifs);
  y = drawSectionTitle(doc, lbl.certifs, y);

  if (allCerts.length === 0) {
    setFont(doc, 'italic', 9); setTxt(doc, C.muted);
    doc.text('Aucune certification renseignée.', MARG, y + 6);
  } else {
    for (const cert of allCerts) {
      if (y > 265) {
        drawFooter(doc, name, pageNum, totalPages); doc.addPage(); pageNum++; totalPages++;
        y = drawHeroCompact(doc, lbl.certifs + ' (suite)');
        y = drawSectionTitle(doc, lbl.certifs, y);
      }
      y = await drawCertCard(doc, cert, y);
    }
  }

  drawFooter(doc, name, pageNum, totalPages);
  const fileRole = role ? role.toLowerCase().replace(/\s+/g, '-') : '';
  const fileName = fileRole
    ? `dossier-competences-${fileRole}-${name.toLowerCase().replace(/\s+/g, '-')}.pdf`
    : `dossier-competences-${name.toLowerCase().replace(/\s+/g, '-')}.pdf`;
  doc.save(fileName);
}

// ── Composant bouton ──────────────────────────────────────────────
export default function DossierButton({
  profile       = {},
  parcoursItems = [],
  parcoursMedia = [],
  experiences   = [],
  technologies  = [],
  lang          = 'fr',
  labels        = {},
  className     = '',
  /**
   * cvUrl : { fr: '/assets/cv-fr.pdf', en: '/assets/cv-en.pdf' }
   * Le bouton CV n'apparaît que si une URL est fournie pour la langue active.
   */
  cvUrl         = {},
}) {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      await generatePDF({ profile, parcoursItems, parcoursMedia, experiences, technologies, lang, labels });
    } catch (err) {
      console.error('[DossierButton] Erreur PDF :', err);
      alert('Erreur lors de la génération du PDF : ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className='comp'>
      <div className="comp-container">
        <p>
          {lang === 'en'
            ? 'Need a skills portfolio or a CV ? Click below.'
            : "Besoin d'un dossier de compétence ou d'un CV ? Cliquez-ci desous"}
        </p>
        <br />
        <div className="dossier">
          {/* Bouton dossier de compétences */}
          <button
            onClick={handleDownload}
            disabled={loading}
            className={`dossier-btn${loading ? ' dossier-btn--loading' : ''} ${className}`}
          >
            {loading
              ? <><Spinner />{lang === 'en' ? 'Generating…' : 'Génération…'}</>
              : <><IconDownload />{lang === 'en' ? 'My skills portfolio (PDF)' : 'Mon dossier de compétences (PDF)'}</>
            }
          </button>

          {/* Bouton CV — affiché uniquement si une URL existe pour la langue courante */}
          {cvUrl[lang] && (
            <a
              href={cvUrl[lang]}
              download
              className={`dossier-btn cv-btn ${className}`}
            >
              <IconCV />
              {lang === 'en' ? 'My CV (PDF)' : 'Mon CV (PDF)'}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function IconDownload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function IconCV() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="16" y2="17"/>
    </svg>
  );
}

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      style={{ animation: 'spin 0.8s linear infinite' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <path d="M12 2a10 10 0 0 1 10 10"/>
    </svg>
  );
}